import * as React from 'react';
import { formatStoryLabel } from './labels';
import type { StoryLabels } from './labels';
import type { StoryPageTheme } from './stories-theme';
import type { StoryLang, StoryUnit, StoryWoman } from './story-types';
import { pickLocalized, storyLangAttr } from './story-utils';

export interface StoryRevealPageProps {
  woman: StoryWoman;
  unit?: StoryUnit | null;
  lang: StoryLang;
  labels: StoryLabels;
  theme: StoryPageTheme;
  /**
   * 'animate' plays the reveal once (teaser "?" → portrait, the poster colour
   * floods in, the redaction bars peel off her name); 'instant' shows the
   * finished page.
   */
  reveal: 'animate' | 'instant';
}

export function storyRevealKicker(labels: StoryLabels, unit?: StoryUnit | null): string {
  return unit ? formatStoryLabel(labels.revealKicker, { n: unit.order }) : labels.revealKickerNoUnit;
}

/** Her name on one or two lines, and the size that fits (prototype rules). */
export function storyRevealNameLines(name: string): { lines: string[]; fontSize: number } {
  const parts = name.trim().toUpperCase().split(/\s+/).filter(Boolean);
  const lines = parts.length > 2 ? [parts[0], parts.slice(1).join(' ')] : parts;
  const longest = lines.reduce((n, l) => Math.max(n, l.length), 0);
  return { lines, fontSize: longest > 16 ? 34 : longest > 10 ? 44 : 60 };
}

/** Fixed first page of every story: who she is. */
export function StoryRevealPage({ woman, unit, lang, labels, theme, reveal }: StoryRevealPageProps) {
  // The animation is a class flip two frames after mount, so the browser
  // paints the "before" state first.
  const [go, setGo] = React.useState(reveal === 'instant');
  React.useEffect(() => {
    if (reveal === 'instant') {
      setGo(true);
      return;
    }
    if (typeof requestAnimationFrame !== 'function') {
      setGo(true);
      return;
    }
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setGo(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [reveal]);

  const { lines, fontSize } = storyRevealNameLines(woman.name);
  const field = pickLocalized({ en: woman.field ?? '', pt: woman.fieldPt ?? '' }, lang).text;
  const nationality = pickLocalized({ en: woman.nationality ?? '', pt: woman.nationalityPt ?? '' }, lang).text;
  const tagline = pickLocalized(woman.tagline, lang);
  const meta = [field, nationality].filter(Boolean).join(' · ');

  return (
    <div
      className={`zs-rv${go ? ' zs-go' : ''}${reveal === 'instant' ? ' zs-instant' : ''}`}
      data-testid="story-reveal"
      style={{ '--zs-ubg': theme.ubg, '--zs-uhead': theme.uhead, '--zs-ufg': theme.ufg, '--zs-ufg-soft': theme.ufgSoft } as React.CSSProperties}
    >
      <div className="zs-flood" />
      <div className="zs-pg-in">
        <h2 className="zs-kick">{storyRevealKicker(labels, unit)}</h2>
        <div className="zs-medal">
          <div className="zs-q" aria-hidden="true">
            ?
          </div>
          <div className="zs-ph">
            {woman.portraitUrl ? (
              <img src={woman.portraitUrl} alt={woman.name} draggable={false} />
            ) : (
              <div className="zs-ini" aria-hidden="true">
                {woman.name.trim().charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="zs-tp" style={{ top: -4, left: 18, transform: 'rotate(-14deg)' }} />
          <div className="zs-tp" style={{ top: 4, right: 6, transform: 'rotate(18deg)' }} />
        </div>
        <div className="zs-nm1" style={{ fontSize }} data-testid="story-reveal-name">
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 ? <br /> : null}
              <span>
                {line}
                <i className="zs-bar" aria-hidden="true" />
              </span>
            </React.Fragment>
          ))}
        </div>
        {meta ? <div className="zs-rmeta">{meta}</div> : null}
        {woman.years ? <div className="zs-yrs">{woman.years}</div> : null}
        <div className="zs-hint" aria-hidden="true">
          <span className="zs-h1">{labels.hintBefore}</span>
          <span className="zs-h2">{labels.hintAfter}</span>
        </div>
        <p className="zs-line" lang={storyLangAttr(tagline.lang)} data-testid="story-reveal-line">
          {tagline.text}
        </p>
      </div>
    </div>
  );
}
